import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
  Checkbox,
  FormControlLabel,
  Menu,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ClearIcon from "@mui/icons-material/Clear";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { CustomJsonEditor } from "components/Nav/Drawers/RightDrawer/FlightDebugger/debuggerComponents/CustomJsonEditor";
import { useQuery, gql } from "@apollo/client";
import {
  WORK_SCHEDULE_CONFIG_FULL_QUERY,
  SINGLE_SENSE_CONFIG_QUERY,
  midgardClients,
} from "providers/MidgardProvider";
import useWindowSize from "utils/customHooks/useWindowSize";
import { exportSkuTableToCsv } from "./SkuTable/skuTableExport";
import {
  prepareSkuTableData,
  SkuTableDataType,
} from "./SkuTable/skuTableUtils";
import { SkuTable } from "./SkuTable/SkuTable";
import { useSelector } from "store/useSelector";
import { Info, MoreVert } from "@mui/icons-material";
import { LabelWithSubtitleStyles } from "components/Settings/SettingsStyles";
import { useBreakpoints } from "constants/constants";
import { isEqual } from "lodash";

// List of customers with exceptionally large SKU datasets
const CUSTOMERS_WITH_TOO_MANY_SKUS = [
  "meritmedical-southjordan",
  "meritmedical-galway",
  "meritmedical-tijuana",
  "sojo-industries",
  "sbdwarrington",
];

// Query to get all SKUs with station data
const SKUS_WITH_STATIONS_QUERY = gql`
  query SkusWithStations($limit: Int, $offset: Int, $searchQuery: String) {
    skusWithStations(
      activeOnly: true
      limit: $limit
      offset: $offset
      searchQuery: $searchQuery
    ) {
      __typename
      opSkuStationId
      skuId
      name
      unit
      type
      group
      station {
        id
        name
        __typename
      }
      active
      rates {
        teamSize
        targetRate
        timeUnit
        rateType
        skuScale
        rateString
        __typename
      }
    }
  }
`;

// Query to get all processes
const PROCESSES_QUERY = gql`
  query ProcessessQuery {
    processes {
      description
      legacyStationName
      legacyStationId
    }
  }
`;

// Type for process info
type ProcessInfo = {
  processId: string;
  name: string;
  workScheduleName?: string;
  processGroupName?: string;
  lifecycleStatus?: string;
};

interface PlaybookData {
  processId: string;
  skuCode?: string;
  playbook?: any;
  senseConfig?: any;
}

interface SkuStationData {
  id: string;
  isTimePerUnit: boolean;
  name: string;
  skuScale: string;
  targetRate: number;
  timeWindow: number;
  teamSize: number;
  // Optional fields for error handling
  isPartialData?: boolean;
  errorMessage?: string;
}

export const SkuTableSection = memo(() => {
  const { translate } = useFlightTranslation();
  const cKey = useCustomerKey();
  // Check if customer is in the list of those with too many SKUs
  const hasTooManySkus = CUSTOMERS_WITH_TOO_MANY_SKUS.includes(cKey);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Get all process groups from Redux store
  const allGroups = useSelector(s => s.legacy.allGroups);

  useEffect(() => {
    if (allGroups) {
      // Debug the allGroups structure
      console.log(
        "allGroups structure:",
        allGroups,
        Object.keys(allGroups).map(key => ({
          groupName: key,
          shortName: allGroups[key].shortName,
          processCount: allGroups[key].processes?.length || 0,
          sampleProcess: allGroups[key].processes?.[0] || null,
        }))
      );
    }
  }, [allGroups]);

  const [availableProcesses, setAvailableProcesses] = useState<ProcessInfo[]>(
    []
  );
  const [organizedProcesses, setOrganizedProcesses] = useState<{
    [workSchedule: string]: { [processGroup: string]: ProcessInfo[] };
  }>({});
  const [selectedProcessId, setSelectedProcessId] = useState<string>("all");
  // Track the selected work schedule
  const [selectedWorkSchedule, setSelectedWorkSchedule] =
    useState<string>("all");
  const [playbookData, setPlaybookData] = useState<PlaybookData | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  // Raw SKU data from the API
  const [allSkusData, setAllSkusData] = useState<any[]>([]);
  // Store a backup of the full all-SKUs processed data for reference
  const [allSkusBackupData, setAllSkusBackupData] = useState<
    SkuTableDataType[]
  >([]);

  const [skuStationData, setSkuStationData] = useState<
    Map<string, SkuStationData[]>
  >(new Map());

  // State to track when playbook data is loading
  const [exportingCsv, setExportingCsv] = useState(false);
  const [loadingAllSkusForCsv, setLoadingAllSkusForCsv] = useState(false);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const { innerHeight } = useWindowSize();

  // State to track pagination settings for SKUs query
  const [paginationEnabled, setPaginationEnabled] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // State to track search term with debouncing
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Debounce search term changes
  useEffect(() => {
    if (!paginationEnabled || selectedProcessId !== "all") {
      // Don't debounce if not using server-side pagination
      setDebouncedSearchTerm(searchTerm);
      return;
    }

    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, paginationEnabled, selectedProcessId]);

  // Query to get all SKUs with station data
  const {
    data: skusData,
    loading: loadingSkus,
    error: skusError,
    refetch: refetchSkus,
  } = useQuery(SKUS_WITH_STATIONS_QUERY, {
    client: midgardClients.get(cKey),
    variables: paginationEnabled
      ? {
          limit: rowsPerPage,
          offset: currentPage * rowsPerPage,
          ...(debouncedSearchTerm ? { searchQuery: debouncedSearchTerm } : {}),
        }
      : {}, // No pagination if disabled or not showing all SKUs
    onError: err => {
      console.error("Error fetching SKUs with stations:", err);
      setError("Failed to load SKUs. Please try again later.");
    },
    fetchPolicy: "network-only", // Don't use cache for this query
  });

  // Query to fetch all processes
  const {
    data: processesData,
    loading: _processesLoading,
    error: _processesError,
  } = useQuery(PROCESSES_QUERY, {
    client: midgardClients.get(cKey),
    fetchPolicy: "network-only",
    onError: err => {
      console.error("Error fetching processes:", err);
    },
  });

  // Map of station IDs to process descriptions
  const [stationToProcessMap, setStationToProcessMap] = useState<
    Record<string, string>
  >({});

  // Process the processes data to create a mapping of station IDs to process descriptions
  useEffect(() => {
    if (processesData?.processes) {
      const mapping: Record<string, string> = {};

      processesData.processes.forEach((process: any) => {
        if (process.legacyStationId) {
          mapping[process.legacyStationId] =
            process.description || process.legacyStationName || "";
        }
      });
      if (!isEqual(mapping, stationToProcessMap)) {
        setStationToProcessMap(mapping);
        console.log("Station to Process mapping:", mapping);
      }
    }
  }, [processesData, stationToProcessMap]);

  // Handle pagination changes
  const handlePageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);

      // Refetch data when page changes if pagination is enabled and showing all SKUs
      if (paginationEnabled && selectedProcessId === "all") {
        // Always include search term when changing pages
        refetchSkus({
          limit: rowsPerPage,
          offset: newPage * rowsPerPage,
          ...(debouncedSearchTerm ? { searchQuery: debouncedSearchTerm } : {}),
        });
      }
    },
    [
      paginationEnabled,
      selectedProcessId,
      rowsPerPage,
      refetchSkus,
      debouncedSearchTerm,
    ]
  );

  const handleRowsPerPageChange = useCallback(
    (newRowsPerPage: number) => {
      setRowsPerPage(newRowsPerPage);
      setCurrentPage(0); // Reset to first page when changing rows per page

      // Refetch data when rows per page changes if pagination is enabled and showing all SKUs
      if (paginationEnabled && selectedProcessId === "all") {
        refetchSkus({
          limit: newRowsPerPage,
          offset: 0,
          ...(debouncedSearchTerm ? { searchQuery: debouncedSearchTerm } : {}),
        });
      }
    },
    [paginationEnabled, selectedProcessId, refetchSkus, debouncedSearchTerm]
  );

  // Also refetch when pagination is toggled
  useEffect(() => {
    if (selectedProcessId === "all") {
      if (paginationEnabled) {
        refetchSkus({
          limit: rowsPerPage,
          offset: currentPage * rowsPerPage,
          ...(debouncedSearchTerm ? { searchQuery: debouncedSearchTerm } : {}),
        });
      } else {
        // When turning off pagination, show loading state until data is fetched
        setIsPaginationLoading(true);
        refetchSkus({}); // Fetch all without pagination
      }
    }
  }, [
    paginationEnabled,
    selectedProcessId,
    currentPage,
    rowsPerPage,
    refetchSkus,
    debouncedSearchTerm,
  ]);

  // Reset pagination loading state when data is loaded
  useEffect(() => {
    if (isPaginationLoading && !loadingSkus) {
      setIsPaginationLoading(false);
    }
  }, [isPaginationLoading, loadingSkus]);

  // Store all SKUs data when it's loaded
  useEffect(() => {
    if (!loadingSkus && skusData && !skusError) {
      setAllSkusData(skusData.skusWithStations || []);
    }
  }, [loadingSkus, skusData, skusError]);

  // First query: Get all work schedules
  const {
    data: scheduleData,
    loading: loadingSchedules,
    error: scheduleError,
  } = useQuery(WORK_SCHEDULE_CONFIG_FULL_QUERY, {
    client: midgardClients.get(cKey),
    onError: err => {
      console.error("Error fetching work schedule config list:", err);
      setError(
        "Failed to load work schedule configurations. Please try again later."
      );
      setLoading(false);
    },
    fetchPolicy: "network-only", // Don't use cache for this query
  });

  // Extract process IDs from work schedules
  useEffect(() => {
    if (!loadingSchedules && scheduleData && !scheduleError) {
      try {
        const schedules = scheduleData.workScheduleConfigList || [];

        // Extract process IDs from all schedules
        const processes: ProcessInfo[] = [];
        schedules.forEach((schedule: any) => {
          const workScheduleName =
            schedule.name || translate("Unnamed Work Schedule");

          if (schedule.processes) {
            schedule.processes.forEach((process: any) => {
              if (process.taps && process.taps.length > 0) {
                process.taps.forEach((tap: any) => {
                  if (tap.machemetryId) {
                    // Find which group this process belongs to in allGroups
                    let processGroupName = translate("Ungrouped");

                    // Loop through allGroups to find which group contains this processId
                    if (allGroups?.length > 0) {
                      const machemetryId = tap.machemetryId;
                      let foundGroup = false;

                      // The allGroups structure has groups with processes that each have an id
                      // First, create a flat list of all groups with their processes for easier debugging
                      const processToGroupMap = Object.entries(
                        allGroups
                      ).reduce(
                        (acc, [groupName, groupData]: [string, any]) => {
                          if (groupData && Array.isArray(groupData.processes)) {
                            groupData.processes.forEach((p: any) => {
                              if (p && p.id) {
                                acc[p.id] = {
                                  groupName,
                                  shortName: groupData.shortName || groupName,
                                  processId: p.id,
                                };
                              }
                            });
                          }
                          return acc;
                        },
                        {} as Record<
                          string,
                          {
                            groupName: string;
                            shortName: string;
                            processId: string;
                          }
                        >
                      );

                      // Now look for an exact match for this process
                      if (processToGroupMap[machemetryId]) {
                        processGroupName =
                          processToGroupMap[machemetryId].shortName;
                        foundGroup = true;
                      } else {
                        // If we don't find a direct match, try case-insensitive comparison
                        const processIds = Object.keys(processToGroupMap);
                        const matchingId = processIds.find(
                          id => id.toLowerCase() === machemetryId.toLowerCase()
                        );
                        if (matchingId) {
                          processGroupName =
                            processToGroupMap[matchingId].shortName;
                          foundGroup = true;
                        }
                      }

                      // For debugging only - log unmatched processes to help diagnose the issue
                      if (!foundGroup) {
                        console.log(
                          `Process ${tap.name || machemetryId} (${machemetryId}) not found in any group`
                        );
                      }
                    }

                    processes.push({
                      processId: tap.machemetryId,
                      name: tap.name || tap.machemetryId,
                      workScheduleName,
                      processGroupName,
                      lifecycleStatus: tap.lifecycleStatus || "active",
                    });
                  }
                });
              }
            });
          }
        });

        // Group processes by work schedule and process group
        // First organize by workScheduleName, then by processGroupName
        const processesMap: {
          [workSchedule: string]: { [processGroup: string]: ProcessInfo[] };
        } = {};

        processes.forEach(process => {
          const workSchedule = process.workScheduleName || translate("Other");
          const processGroup = process.processGroupName || translate("Other");

          if (!processesMap[workSchedule]) {
            processesMap[workSchedule] = {};
          }

          if (!processesMap[workSchedule][processGroup]) {
            processesMap[workSchedule][processGroup] = [];
          }

          processesMap[workSchedule][processGroup].push(process);
        });

        // Save both the flat list (for state management) and the organized structure (for rendering)
        setAvailableProcesses(processes);
        setOrganizedProcesses(processesMap);
        setLoading(false);
      } catch (error) {
        console.error("Error processing schedule data:", error);
        setError(
          "Failed to process work schedule data. Please try again later."
        );
        setLoading(false);
      }
    }
  }, [loadingSchedules, scheduleData, scheduleError, allGroups, translate]);

  // Function to fetch playbook for a selected process
  const fetchProcessPlaybook = useCallback(
    async (processId: string) => {
      setLoading(true);
      try {
        const { data } = await midgardClients.get(cKey).query({
          query: SINGLE_SENSE_CONFIG_QUERY,
          variables: { machemetryId: processId },
          fetchPolicy: "network-only",
        });

        // Check for new format with senseConfig (singular)
        if (data && data.senseConfig) {
          const config = data.senseConfig;

          // Check if we have a process playbook
          if (config?.process?.playbook) {
            // Check if playbook is an array (which is the expected format based on the API response)
            if (Array.isArray(config.process.playbook)) {
              // Found playbook data in senseConfig format (array format)
              setPlaybookData({
                processId,
                senseConfig: config,
              });

              // We no longer fetch any SKU data after loading the sense config
              // This simplifies the flow and avoids loading state issues
              console.log(
                `Got playbook data with ${config.process.playbook.length} items - displaying without fetching SKU data`
              );

              // Immediately end the loading state
              setLoading(false);
              return;
            } else {
              console.log("Found legacy playbook (not array)");
              setPlaybookData({
                processId,
                senseConfig: config,
              });
              return;
            }
          }
        }

        // For backwards compatibility, also check the old format with senseConfigs (plural)
        if (data && data.senseConfigs && data.senseConfigs.length > 0) {
          const config = data.senseConfigs[0];

          // Check if we have a process playbook
          if (config.process && config.process.playbook) {
            // Check if this is an array format
            if (Array.isArray(config.process.playbook)) {
              console.log(
                "Found playbook array in legacy senseConfigs format with",
                config.process.playbook.length,
                "items"
              );
            } else {
              console.log(
                "Found playbook in legacy senseConfigs format (not array)"
              );
            }

            setPlaybookData({
              processId,
              senseConfig: config,
            });
            return;
          }

          // Try the old style playbook property
          if (config.playbook) {
            // Check if this is an array format
            if (Array.isArray(config.playbook)) {
              console.log(
                "Found legacy playbook array in senseConfigs with",
                config.playbook.length,
                "items"
              );
            } else {
              console.log("Found legacy playbook in senseConfigs (not array)");
            }

            setPlaybookData({
              processId,
              playbook: config.playbook,
            });
            return;
          }
        }

        // If we get here, we didn't find any playbook
        console.log("No playbook found for process ID:", processId);
        setPlaybookData({
          processId,
          playbook: null,
        });
      } catch (err) {
        console.error("Error fetching process playbook:", err);
        setError("Failed to load process playbook. Please try again later.");
        setPlaybookData({
          processId,
          playbook: null,
        });
      } finally {
        // Always end loading state since we're not doing secondary fetches anymore
        setLoading(false);
      }
    },
    [cKey]
  );
  // Query for selected process playbook
  useEffect(() => {
    if (selectedProcessId) {
      // Set loading state without clearing playbookData immediately
      // This keeps the UI stable during loading
      setLoading(true);
      setError(null);
      setSkuStationData(new Map());

      // Fetch the playbook for the selected process
      fetchProcessPlaybook(selectedProcessId);
    }
  }, [selectedProcessId, fetchProcessPlaybook]);

  // Handle work schedule selection change
  const handleWorkScheduleChange = (event: SelectChangeEvent) => {
    const newWorkSchedule = event.target.value;
    setSelectedWorkSchedule(newWorkSchedule);

    // If currently selected process is not in this work schedule, reset to "all"
    if (newWorkSchedule !== "all" && selectedProcessId !== "all") {
      // Check if the current selected process exists in the selected work schedule
      const processExists = Object.values(
        organizedProcesses[newWorkSchedule] || {}
      )
        .flat()
        .some(process => process.processId === selectedProcessId);

      if (!processExists) {
        setSelectedProcessId("all");
      }
    }
  };

  // Handle process selection change
  const handleProcessChange = (event: SelectChangeEvent) => {
    setSelectedProcessId(event.target.value);
  };

  // Prepare data in a structured format for the editor
  const getJsonData = () => {
    if (!playbookData) {
      return null;
    }

    const play = playbookData.playbook;

    // Find matching SKU data from allSkusData based on skuCode if available
    const matchingSkus = allSkusData.filter(
      sku => sku.skuId === play?.skuCode || sku.name === play?.skuCode
    );

    return {
      ...playbookData,
      matchingSkus,
      skuTableData,
      skusData,
      // Add any additional data processing here
    };
  };

  useEffect(() => {
    // Mark as loaded after the initial load
    if (!loading && !loadingSchedules && !isLoaded) {
      setIsLoaded(true);
    }
  }, [loading, loadingSchedules, isLoaded]);

  // Helper function to process raw SKU data into table format
  const processRawSkuData = useCallback(
    (
      rawSkuData: any[],
      processMap?: Record<string, string>
    ): SkuTableDataType[] => {
      const parsedData: SkuTableDataType[] = [];
      // Use the passed in processMap or fall back to stationToProcessMap
      const stationMap = processMap || stationToProcessMap;

      // Group SKUs by skuId for proper linear scaling detection
      const skuMap: Record<
        string,
        {
          skuId: string;
          name: string;
          type: string;
          station: any;
          rates: any[];
          processName: string;
          teamSizes: number[];
          isLinearScaling: boolean;
          teamSizeMin: number | null;
          teamSizeMax: number | null;
        }
      > = {};

      // First pass: group SKUs by ID and collect rates
      rawSkuData.forEach(sku => {
        const skuId = sku.skuId || sku.name || "unknown";

        if (!skuMap[skuId]) {
          // Initialize a new SKU entry
          skuMap[skuId] = {
            skuId,
            name: sku.name || "",
            type: sku.type || "",
            station: sku.station,
            rates: [],
            processName: "",
            teamSizes: [],
            isLinearScaling: false,
            teamSizeMin: null,
            teamSizeMax: null,
          };
        }

        // Add rates from this SKU instance
        if (sku.rates && Array.isArray(sku.rates)) {
          skuMap[skuId].rates = [...skuMap[skuId].rates, ...sku.rates];

          // Extract team sizes from rates
          sku.rates.forEach((rate: any) => {
            if (
              typeof rate.teamSize === "number" &&
              !isNaN(rate.teamSize) &&
              !skuMap[skuId].teamSizes.includes(rate.teamSize)
            ) {
              skuMap[skuId].teamSizes.push(rate.teamSize);
            }
          });
        }

        // Get process name from station mapping
        const processName =
          sku.station && sku.station.id
            ? stationMap[sku.station.id] || sku.station.name
            : "-";

        // Only update process name if it's not already set or current is more specific
        if (!skuMap[skuId].processName || processName !== "-") {
          skuMap[skuId].processName = processName;
        }
      });

      // Second pass: determine linear scaling and min/max team sizes for each SKU
      Object.values(skuMap).forEach(skuEntry => {
        // Sort team sizes for proper min/max detection
        skuEntry.teamSizes.sort((a, b) => a - b);

        // Set min/max team sizes
        if (skuEntry.teamSizes.length > 0) {
          skuEntry.teamSizeMin = skuEntry.teamSizes[0];
          skuEntry.teamSizeMax =
            skuEntry.teamSizes[skuEntry.teamSizes.length - 1];
        }

        // Detect linear scaling
        let isLinearScaling = false;
        if (
          skuEntry.rates &&
          skuEntry.rates.length > 1 &&
          skuEntry.teamSizes.length > 1
        ) {
          // Create a map of team size to target rate
          const teamSizeToRate: Record<number, number> = {};
          skuEntry.rates.forEach((rate: any) => {
            if (
              typeof rate.teamSize === "number" &&
              !isNaN(rate.teamSize) &&
              typeof rate.targetRate === "number" &&
              !isNaN(rate.targetRate)
            ) {
              teamSizeToRate[rate.teamSize] = rate.targetRate;
            }
          });

          // Check if we have rates for each team size
          const hasAllRates = skuEntry.teamSizes.every(
            size => teamSizeToRate[size] !== undefined
          );

          if (hasAllRates && skuEntry.teamSizes.length >= 2) {
            // For linear scaling, ratios of team size to target rate should be consistent
            // Calculate the ratio for the first team size as our reference
            const firstSize = skuEntry.teamSizes[0];
            const firstRate = teamSizeToRate[firstSize];
            const expectedRatio = firstRate / firstSize;

            // Check if all other team sizes maintain the same ratio
            isLinearScaling = skuEntry.teamSizes.every(size => {
              const rate = teamSizeToRate[size];
              // Allow for some floating point error (within 0.001)
              return Math.abs(rate / size - expectedRatio) < 0.001;
            });
          }
        }

        // Update with calculated linear scaling flag
        skuEntry.isLinearScaling = isLinearScaling;
      });

      // Third pass: create table rows from grouped SKUs
      Object.values(skuMap).forEach(skuEntry => {
        // Find the base rate (using the first rate for display purposes)
        const baseRate =
          skuEntry.rates && skuEntry.rates.length > 0
            ? skuEntry.rates[0]
            : null;

        // Create an entry for this SKU with only the fields that exist in SkuTableDataType
        parsedData.push({
          skuCode: skuEntry.skuId,
          skuName: skuEntry.name,
          stationName: skuEntry.processName,
          scale: baseRate ? baseRate.skuScale : null,
          rate: baseRate ? baseRate.targetRate : null,
          targetRate: baseRate ? baseRate.targetRate : null,
          timeWindow: baseRate ? baseRate.timeUnit : null,
          teamSizes: skuEntry.teamSizes,
          description: skuEntry.type || "",
          isLoading: false,
          isPartialData: false,
          isLinearScaling: skuEntry.isLinearScaling, // Use the calculated linear scaling property
          teamSizeMin: skuEntry.teamSizeMin, // Add the minimum team size
          teamSizeMax: skuEntry.teamSizeMax, // Add the maximum team size
        });
      });

      return parsedData;
    },
    [stationToProcessMap]
  );

  // Update the effect that processes allSkusData to ensure we always save a backup
  useEffect(() => {
    // Process and backup all SKUs data as soon as we get it, regardless of current process selection
    if (allSkusData.length > 0) {
      // Don't use stationToProcessMap directly in this effect, pass it as an argument
      const allSkusProcessed = processRawSkuData(
        allSkusData,
        stationToProcessMap
      );

      // Always update the backup data when we have new SKU data
      setAllSkusBackupData(allSkusProcessed);
    }
  }, [allSkusData, processRawSkuData, stationToProcessMap]);

  // Improve the skuTableData computation to better merge process-specific data with the backup data
  const skuTableData = useMemo(() => {
    // Get the selected process name from organizedProcesses for display
    let selectedProcessName = "";
    if (selectedProcessId !== "all") {
      // Look up the process name in organizedProcesses
      for (const [, workScheduleProcesses] of Object.entries(
        organizedProcesses
      )) {
        for (const [, processes] of Object.entries(workScheduleProcesses)) {
          const foundProcess = processes.find(
            p => p.processId === selectedProcessId
          );
          if (foundProcess && foundProcess.name) {
            selectedProcessName = foundProcess.name;
            break;
          }
        }
        if (selectedProcessName) {
          break;
        }
      }

      // If we couldn't find the process name, use the ID as fallback
      if (!selectedProcessName) {
        selectedProcessName = selectedProcessId;
      }
    }

    // If selectedProcessId is 'all', prepare data directly from allSkusData
    if (selectedProcessId === "all") {
      // First process all SKUs
      const processedData = processRawSkuData(allSkusData, stationToProcessMap);

      // If a work schedule is selected, filter the SKUs to only include those in that work schedule
      if (selectedWorkSchedule && selectedWorkSchedule !== "all") {
        return processedData.filter(sku => {
          // Check if the SKU's station/process belongs to the selected work schedule
          // by looking it up in the organizedProcesses map
          const stationProcess = organizedProcesses[selectedWorkSchedule];
          if (stationProcess && sku.stationName) {
            // Check all process groups in this work schedule
            for (const processGroupName in stationProcess) {
              const processes = stationProcess[processGroupName];
              // Check if any process in this group matches the SKU's station/process
              if (processes.some(process => process.name === sku.stationName)) {
                return true;
              }
            }
          }
          return false;
        });
      }

      return processedData;
    }

    // For process-specific view, prepare data from the playbook
    let processData = prepareSkuTableData(
      playbookData,
      allSkusData,
      skuStationData
    );

    // CRITICAL FIX: When a specific process is selected, ensure all SKUs show that process name
    if (selectedProcessId !== "all" && selectedProcessName) {
      // Update all SKUs to show the selected process name
      processData = processData.map(sku => ({
        ...sku,
        stationName: selectedProcessName, // Set stationName to the selected process name
      }));
    }

    // Always attempt to enrich the process data with our backup data
    if (allSkusBackupData.length > 0) {
      // Create a lookup map for quick access to backup data by SKU code
      const skuBackupMap = new Map<string, SkuTableDataType>();
      allSkusBackupData.forEach(skuData => {
        if (skuData.skuCode) {
          skuBackupMap.set(skuData.skuCode, skuData);
        }
      });

      // For each row in the process data, enrich it with backup data if available
      let result = processData.map(row => {
        const backupData = skuBackupMap.get(row.skuCode);
        if (backupData) {
          return {
            ...row,
            description: row.description || backupData.description,
            skuName: row.skuName || backupData.skuName,
            // Check if scale is "N/A" and use backup data in that case
            scale:
              row.scale === "N/A"
                ? backupData.scale
                : row.scale || backupData.scale,
            // CRITICAL: Preserve the stationName we set earlier for specific processes
            stationName:
              selectedProcessId !== "all"
                ? selectedProcessName
                : row.stationName || backupData.stationName,
            rate: row.rate || backupData.rate,
            targetRate: row.targetRate ?? backupData.targetRate,
            timeWindow: row.timeWindow ?? backupData.timeWindow,
            teamSizes: row.teamSizes || backupData.teamSizes,
          };
        }
        return row;
      });

      // One final check to guarantee stationName is correct for selected process
      if (selectedProcessId !== "all" && selectedProcessName) {
        result = result.map(sku => ({
          ...sku,
          stationName: selectedProcessName,
        }));
      }

      return result;
    }

    return processData;
  }, [
    selectedProcessId,
    selectedWorkSchedule,
    playbookData,
    allSkusData,
    skuStationData,
    allSkusBackupData,
    processRawSkuData,
    stationToProcessMap,
    organizedProcesses, // Added explicitly to handle it in the function rather than creating a closure
  ]);
  const { isTabletOrLarger } = useBreakpoints();
  const [settingsAnchorEl, setSettingsAnchorEl] =
    React.useState<null | HTMLElement>(null);

  const selectedProcessName = useMemo(() => {
    const allProcesses = Object.values(organizedProcesses).reduce(
      (acc, processGroups) => [...acc, ...Object.values(processGroups).flat()],
      [] as ProcessInfo[]
    );
    return selectedProcessId === "all"
      ? "All"
      : allProcesses.find(process => process.processId === selectedProcessId)
          ?.name || "";
  }, [selectedProcessId, organizedProcesses]);

  // Add a function to handle search term changes
  const handleSearchChange = useCallback(
    (newSearchTerm: string) => {
      setSearchTerm(newSearchTerm);

      // Reset to first page when search term changes
      if (paginationEnabled && selectedProcessId === "all") {
        setCurrentPage(0);
      }
    },
    [paginationEnabled, selectedProcessId]
  );

  const fetchAllSkusForCsv = useCallback(async () => {
    if (!paginationEnabled || selectedProcessId !== "all") {
      // If not using server-side pagination, just use the existing data
      return skuTableData;
    }

    try {
      setLoadingAllSkusForCsv(true);

      // Make a fresh query to get all SKUs without pagination
      const result = await refetchSkus({
        limit: 5000, // Very large limit to get all SKUs
        offset: 0,
        ...(debouncedSearchTerm ? { searchQuery: debouncedSearchTerm } : {}),
      });

      if (result?.data?.skusWithStations) {
        // Process the data directly with the current stationToProcessMap
        const allData = processRawSkuData(
          result.data.skusWithStations,
          stationToProcessMap
        );
        return allData;
      } else {
        throw new Error("Failed to fetch all SKUs for CSV export");
      }
    } catch (error) {
      console.error("Error fetching all SKUs for CSV export:", error);
      setError("Failed to fetch all SKUs for CSV export. Please try again.");
      return []; // Return empty array instead of null
    } finally {
      setLoadingAllSkusForCsv(false);
    }
  }, [
    paginationEnabled,
    selectedProcessId,
    refetchSkus,
    debouncedSearchTerm,
    processRawSkuData,
    skuTableData,
    stationToProcessMap, // Added explicitly to handle it in the function rather than creating a closure
  ]);

  if ((loading || loadingSchedules) && !isLoaded) {
    // ... (rest of the code remains the same)
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5">{translate("SKUs")}</Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body2">
            {translate("Loading processes...")}
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5">{translate("SKUs")}</Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {translate("SKUs")}
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: isTabletOrLarger ? "row" : "column",
        }}
      >
        {/* Work Schedule Picker - only show if we have multiple work schedules */}
        {scheduleData && scheduleData.workScheduleConfigList.length > 1 && (
          <FormControl fullWidth>
            <InputLabel id="work-schedule-select-label">
              {translate("Work Schedule")}
            </InputLabel>
            <Select
              labelId="work-schedule-select-label"
              value={selectedWorkSchedule}
              label={translate("Work Schedule")}
              onChange={handleWorkScheduleChange}
              endAdornment={
                <Box
                  sx={{
                    visibility:
                      selectedWorkSchedule !== "all" ? "visible" : "hidden",
                    position: "absolute",
                    right: 28,
                    top: 0,
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      setSelectedWorkSchedule("all");
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Box>
              }
            >
              <MenuItem value="all">{translate("All Work Schedules")}</MenuItem>
              {scheduleData?.workScheduleConfigList
                ?.slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(workSchedule => (
                  <MenuItem
                    key={`ws-option-${workSchedule.name}`}
                    value={workSchedule.name}
                  >
                    {workSchedule.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}

        {/* Process Selector */}
        <FormControl fullWidth>
          <InputLabel id="process-select-label">
            {translate("Process")}
          </InputLabel>
          <Select
            labelId="process-select-label"
            value={selectedProcessId}
            label={translate("Select Process")}
            onChange={handleProcessChange}
            endAdornment={
              <Box
                sx={{
                  visibility:
                    selectedProcessId !== "all" ? "visible" : "hidden",
                  position: "absolute",
                  right: 28,
                  top: 0,
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedProcessId("all");
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Box>
            }
          >
            <MenuItem value="all">{translate("All Processes")}</MenuItem>

            {/* Group processes by work schedule and process group */}
            {/* Flatten the structure to avoid using React.Fragment inside Select */}
            {Object.keys(organizedProcesses)
              .filter(workScheduleName => {
                // Only show processes from the selected work schedule if one is selected
                return (
                  selectedWorkSchedule === "all" ||
                  workScheduleName === selectedWorkSchedule
                );
              })
              .sort()
              .flatMap(workScheduleName => {
                // Start with the work schedule header
                const result: React.ReactNode[] = [];

                // Only show the work schedule header if we're viewing all work schedules
                if (selectedWorkSchedule === "all") {
                  result.push(
                    <MenuItem
                      key={`ws-${workScheduleName}`}
                      disabled
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#f5f5f5",
                        borderBottom: "1px solid #ddd",
                        borderTop: "1px solid #ddd",
                        my: 1,
                        pointerEvents: "none",
                      }}
                    >
                      {workScheduleName}
                    </MenuItem>
                  );
                }

                // Then add each process group and its processes
                Object.keys(organizedProcesses[workScheduleName])
                  .sort()
                  .forEach(processGroupName => {
                    // Add the process group header
                    result.push(
                      <MenuItem
                        key={`pg-${workScheduleName}-${processGroupName}`}
                        disabled
                        sx={{
                          pl: 4,
                          fontWeight: "bold",
                          fontStyle: "italic",
                          fontSize: "0.9rem",
                          color: "text.secondary",
                          pointerEvents: "none",
                        }}
                      >
                        {processGroupName}
                      </MenuItem>
                    );

                    // Add all processes in this group
                    organizedProcesses[workScheduleName][
                      processGroupName
                    ].forEach(process => {
                      // Check if process is decommissioned
                      const isDecommissioned =
                        process.lifecycleStatus === "decommissioned";

                      result.push(
                        <MenuItem
                          key={`process-${process.processId}`}
                          value={process.processId}
                          sx={{
                            pl: 6,
                            // Apply gray color for decommissioned processes
                            color: isDecommissioned
                              ? "text.disabled"
                              : "inherit",
                          }}
                        >
                          {process.name || process.processId}
                          {isDecommissioned && (
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.disabled"
                              sx={{ ml: 1, fontStyle: "italic" }}
                            >
                              {translate("(decommissioned)")}
                            </Typography>
                          )}
                        </MenuItem>
                      );
                    });
                  });

                return result;
              })}
          </Select>
        </FormControl>
      </Box>

      {availableProcesses.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          {translate(
            "No processes found. Check your work schedule configuration."
          )}
        </Alert>
      ) : !playbookData ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          {translate("Select a process to view its SKU configuration.")}
        </Alert>
      ) : (
        // Always attempt to show data if we have playbookData, even if opSkuStation failed
        <Box sx={{ mt: 2 }}>
          {/* Only show this warning when we have confirmed there is no playbook content at all AND we're not loading */}
          {selectedProcessId !== "all" &&
            playbookData &&
            !loading /* Don't show warning during loading state */ &&
            !playbookData.playbook &&
            !(playbookData.senseConfig?.process?.playbook?.length > 0) && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                {translate("No playbook found for the selected process.")}
              </Alert>
            )}

          {/* Show notice when opSkuStation queries are failing but we still have playbook data */}
          {skuStationData.size > 0 &&
            Array.from(skuStationData.values()).some(stations =>
              stations.some(station => station.isPartialData)
            ) && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {translate(
                  "Some station data could not be loaded. Displaying available information."
                )}
              </Alert>
            )}
          {/* SKUs Table */}
          <Box sx={{ mb: 4, overflowX: "auto" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" gutterBottom>
                {selectedProcessId === "all"
                  ? translate("All SKUs")
                  : translate("SKUs for ") + selectedProcessName}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {/* Settings Menu Button */}
                <>
                  <Tooltip title={translate("Table Settings")}>
                    <IconButton
                      size="small"
                      onClick={e => {
                        setSettingsAnchorEl(e.currentTarget);
                      }}
                      sx={{ mr: 1 }}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <SkuTableSettingsMenu
                    settingsAnchorEl={settingsAnchorEl}
                    setSettingsAnchorEl={setSettingsAnchorEl}
                    setPaginationEnabled={setPaginationEnabled}
                    setCurrentPage={setCurrentPage}
                    skuTableData={skuTableData}
                    selectedProcessId={selectedProcessId}
                    playbookData={playbookData}
                    hasTooManySkus={hasTooManySkus}
                    isLoading={loadingSkus || isPaginationLoading || loading}
                    paginationEnabled={paginationEnabled}
                    fetchAllSkusForCsv={fetchAllSkusForCsv}
                    exportSkuTableToCsv={exportSkuTableToCsv}
                    setError={setError}
                    loadingAllSkusForCsv={loadingAllSkusForCsv}
                    exportingCsv={exportingCsv}
                    setExportingCsv={setExportingCsv}
                  />
                </>
              </Box>
            </Box>

            {/* SKU Table - Using TanStack React Table */}
            <Box sx={{ mb: 4, overflowX: "auto" }}>
              <SkuTable
                data={skuTableData}
                loading={
                  loading ||
                  (selectedProcessId === "all" &&
                    (loadingSkus || isPaginationLoading))
                }
                page={currentPage}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                isPaginationServer={
                  paginationEnabled && selectedProcessId === "all"
                }
                totalCount={
                  paginationEnabled && selectedProcessId === "all"
                    ? -1
                    : (skuTableData || []).length
                } // We don't know total count from API, assume large number
                onSearchChange={handleSearchChange}
                globalFilter={searchTerm}
              />
            </Box>

            {/* JSON Editor */}
            {process.env.NODE_ENV === "development" && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  * JSON viewer below is only visible in development mode
                </Typography>
                <CustomJsonEditor
                  key={`json-editor-${selectedProcessId}-${JSON.stringify(skuTableData)}`}
                  value={getJsonData() || {}}
                  height={innerHeight - 300}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
});

function SkuTableSettingsMenu({
  settingsAnchorEl,
  setSettingsAnchorEl,
  skuTableData,
  selectedProcessId,
  playbookData,
  hasTooManySkus,
  isLoading,
  paginationEnabled,
  setCurrentPage,
  fetchAllSkusForCsv,
  exportSkuTableToCsv,
  setError,
  loadingAllSkusForCsv,
  exportingCsv,
  setExportingCsv,
  setPaginationEnabled,
}: {
  settingsAnchorEl: HTMLElement | null;
  setSettingsAnchorEl: (anchorEl: HTMLElement | null) => void;
  skuTableData: SkuTableDataType[];
  selectedProcessId: string;
  playbookData: PlaybookData | null;
  hasTooManySkus: boolean;
  isLoading: boolean;
  paginationEnabled: boolean;
  setCurrentPage: (page: number) => void;
  fetchAllSkusForCsv: () => Promise<SkuTableDataType[]>;
  exportSkuTableToCsv: (data: SkuTableDataType[], fileName: string) => void;
  setError: (error: string) => void;
  loadingAllSkusForCsv: boolean;
  exportingCsv: boolean;
  setExportingCsv: (exporting: boolean) => void;
  setPaginationEnabled: (enabled: boolean) => void;
}) {
  const { translate } = useFlightTranslation();
  return (
    <Menu
      anchorEl={settingsAnchorEl}
      open={Boolean(settingsAnchorEl)}
      onClose={() => setSettingsAnchorEl(null)}
      PaperProps={{
        elevation: 3,
        sx: { minWidth: 220, maxWidth: 280 },
      }}
      disableScrollLock={true}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <MenuItem
        sx={{
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <LabelWithSubtitleStyles
          style={{
            pointerEvents: isLoading ? "none" : "auto",
            cursor: isLoading ? "not-allowed" : "auto",
          }}
          onClick={() => {
            if (isLoading) {
              return;
            }
            setPaginationEnabled(!paginationEnabled);
            // Reset to first page when toggling pagination
            setCurrentPage(0);
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={paginationEnabled}
                size="small"
                disabled={isLoading} // Disable checkbox while loading
              />
            }
            label={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  pointerEvents: "none",
                }}
              >
                <span>{translate("Use pagination")}</span>
                {isLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
              </Box>
            }
          />
          <div className="subtitle">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: "inherit",
                fontSize: "0.75rem",
              }}
            >
              {translate("Use pagination for better performance")}
            </Box>
          </div>
        </LabelWithSubtitleStyles>
      </MenuItem>
      <MenuItem
        onClick={async () => {
          // Don't close the menu right away - keep it open during loading
          // setSettingsAnchorEl(null); - Removed this line

          // Show loading state for CSV export
          setExportingCsv(true);

          try {
            let dataToExport = skuTableData;

            // If using pagination for All SKUs view, fetch all results first
            if (paginationEnabled && selectedProcessId === "all") {
              dataToExport = await fetchAllSkusForCsv();
              // Check if we got valid data
              if (!dataToExport || dataToExport.length === 0) {
                console.warn("No SKUs found for export");
                setError("No SKUs found for export");
                setExportingCsv(false);
                return;
              }
            }

            // Close the menu now that loading is done and we're ready to download
            setSettingsAnchorEl(null);

            // Use our enhanced CSV exporter which properly formats the data
            exportSkuTableToCsv(
              dataToExport,
              selectedProcessId === "all"
                ? `sku_table_export.csv`
                : `sku_plays_${selectedProcessId}.csv`
            );
          } catch (error) {
            console.error("Error preparing CSV data:", error);
            setError("Failed to prepare CSV data. Please try again later.");
            // Close menu on error too
            setSettingsAnchorEl(null);
          } finally {
            setExportingCsv(false);
          }
        }}
        disabled={
          exportingCsv ||
          loadingAllSkusForCsv ||
          isLoading ||
          (selectedProcessId !== "all" &&
            !playbookData?.playbook &&
            !(
              playbookData?.senseConfig?.process?.playbook &&
              Array.isArray(playbookData.senseConfig.process.playbook) &&
              playbookData.senseConfig.process.playbook.length > 0
            ))
        }
        sx={{
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <LabelWithSubtitleStyles>
          <Box className="label" sx={{ display: "flex", alignItems: "center" }}>
            <FileDownloadIcon fontSize="small" sx={{ mr: 0.5 }} />
            {loadingAllSkusForCsv ? (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                {translate("Fetching all SKUs...")}
              </Box>
            ) : (
              translate("Download CSV")
            )}
          </Box>
          {hasTooManySkus && paginationEnabled && (
            <div className="subtitle">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "warning.main",
                  fontSize: "0.75rem",
                }}
              >
                <Info fontSize="small" sx={{ mr: 0.5 }} />
                {translate("SKU list is very long, please be patient")}
              </Box>
            </div>
          )}
        </LabelWithSubtitleStyles>
      </MenuItem>
    </Menu>
  );
}
